/**
 * Task dialog: build a folder, look at it, then submit it.
 *
 * A folder in the tree is one Asana task, and this is the whole loop for it:
 *
 *   Build       → build every SIP under its delivery name and mirror them onto
 *                 the task's S3 folder. Fix a snapshot, build again — the same
 *                 objects are overwritten, so a link already shared with someone
 *                 keeps showing the current version.
 *   Preview All → open those builds, one tab each, when the reviewer asks for
 *                 them.
 *   Test        → compose the whole delivery and send none of it: the Drive copy
 *                 runs as a dry run and the Asana comment comes back unposted,
 *                 so the thing that is hardest to check is the easiest to read.
 *   Submit      → send those exact files to the AppLovin preview tool and Google
 *                 Drive, then comment on the Asana task with both links.
 *
 * Submit is deliberately gated behind a build: what a person approved on screen
 * is what leaves the building, and nothing is rebuilt in between.
 *
 * Neither step is waited for here. Build and Submit hand the work to the
 * editor's jobs (useSnapshotJobs) and this dialog can be closed the moment one
 * starts: the job carries on in the agent container, says how it went through
 * the snackbar, and leaves its result to be picked up the next time this folder
 * is opened. So a second folder can be built while the first is still going —
 * which the container has always allowed, since it locks per task.
 *
 * A task does not stay delivered. Client feedback arrives, the snapshots are
 * fixed, and it goes out again — so every submit runs all three steps and the
 * button reads "Re-deliver" once one has completed. Two things do NOT get
 * cleaned up on the way, because nothing here should delete a file a client may
 * already have opened: a SIP that ships under a new name leaves its old copy in
 * Drive, and a renamed Asana task delivers into a new Drive folder while the
 * old one keeps its contents. Both are worked out before the button is pressed
 * and named in the banner, so the tidying is a deliberate human act.
 *
 * THIS IS THE AUDIENCE+ DIALOG. A folder whose Asana task is in ACS Ads Manager
 * gets dialogDeliveryTaskAdsManager.js instead (see dialogBuildSubmit.js); this
 * one goes once Audience+ is retired.
 *
 * The Asana binding sits in here rather than in a settings screen because it is
 * only ever needed at submit time — a folder builds perfectly well without one
 * — and because this is where the failure ("which of these two tasks?") shows
 * up.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import BuildWaitLine from "../buildWaitLine";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";
import LaunchIcon from "@material-ui/icons/Launch";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import {
  bindAsanaTask,
  asanaCreatorContext as fetchAsanaCreatorContext,
  deliverTask,
  describeJobFailureWithReasons,
  jobFailureCode,
  listAsanaTasks,
  mutateSnapshot,
  previewTask,
} from "../../modules/snapshotEditorApi";
import { collectFolderPaths, normalizeFolderPath } from "../../modules/snapshotTree";
import { BAR_BG, CONTROL_BG, CONTROL_BORDER, FIELD_LABEL, PANEL_BG } from "./panelPalette";
import { formatBytes, formatStamp } from "./taskDeliveryFormat";
import TaskReviewButton from "./taskReviewButton";

// Re-exported: they lived here before both task dialogs needed them.
export { formatBytes, formatStamp };

const STEP_LABELS = {
  plec: "AppLovin preview tool",
  drive: "Google Drive",
  asana: "Asana comment",
};

const BORDER = "1px solid rgba(255,255,255,0.09)";

/** The dashboard's accent (MUI `primary.main`), for the one thing being changed. */
const ACCENT = "#4eabb1";
const ACCENT_SOFT = "rgba(78,171,177,0.4)";

const useStyles = makeStyles({
  // Sections, each introduced by a small caps label. The dialog used to be one
  // long column of sentences; the work here is grouping, not decoration.
  section: { marginTop: "20px" },
  sectionHead: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: "10px",
    marginBottom: "8px",
  },
  sectionTitle: {
    fontSize: "11px",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: FIELD_LABEL,
  },
  card: { border: BORDER, borderRadius: "6px", padding: "10px 12px" },
  // The one card that is the answer to "what am I about to send" — raised off
  // the panel rather than outlined on it, which is what BAR_BG is for.
  cardRaised: { backgroundColor: BAR_BG, border: BORDER, borderRadius: "8px", padding: "12px 14px" },

  // What is about to be delivered, in one glance.
  summaryName: { fontSize: "15px", fontWeight: 500, wordBreak: "break-word", lineHeight: 1.4 },
  chips: { display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "8px" },
  chip: {
    fontSize: "11px",
    padding: "2px 8px",
    borderRadius: "10px",
    border: BORDER,
    backgroundColor: "rgba(0,0,0,0.18)",
    color: FIELD_LABEL,
    whiteSpace: "nowrap",
  },
  chipOk: { color: "#a5d6a7", borderColor: "rgba(165,214,167,0.35)" },
  chipWarn: { color: "#e0b96a", borderColor: "rgba(224,185,106,0.35)" },

  // Delivery names run long — the whole point of this list is reading them, so
  // they wrap instead of being cut off with an ellipsis.
  fileEntry: {
    padding: "8px 6px",
    borderRadius: "5px",
    transition: "background-color 0.15s",
    "&:hover": { backgroundColor: "rgba(255,255,255,0.03)" },
  },
  fileRow: { display: "flex", gap: "10px", alignItems: "flex-start" },
  fileRowDivided: { borderTop: BORDER },
  // The two tokens that belong to the individual creative rather than the
  // batch, on a rule in the accent so they read as an edit of the name above
  // rather than as more file metadata. Only while the naming section is open:
  // the file list is read constantly and edited rarely.
  perFileRow: {
    display: "flex",
    gap: "8px",
    alignItems: "flex-start",
    margin: "10px 0 2px 56px",
    paddingLeft: "12px",
    borderLeft: `2px solid ${ACCENT_SOFT}`,
  },
  perFileLabel: {
    flex: "0 0 auto",
    paddingTop: "11px",
    fontSize: "10px",
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color: FIELD_LABEL,
    opacity: 0.7,
  },
  perFileField: { flex: "0 0 150px" },
  fileLabel: { flex: "0 0 50px", fontSize: "11px", color: FIELD_LABEL, paddingTop: "3px" },
  fileName: {
    flex: 1,
    minWidth: 0,
    fontFamily: "monospace",
    fontSize: "12px",
    lineHeight: 1.55,
    wordBreak: "break-all",
    whiteSpace: "normal",
  },
  // A name read as its tokens: the separators recede so the words are what the
  // eye lands on, and only what CHANGED carries the accent. This replaced
  // turning a whole renamed name green, which said a rename happened without
  // ever saying which word it touched.
  nameSeparator: { opacity: 0.28 },
  nameTokenChanged: { color: ACCENT, fontWeight: 600 },
  fileSize: {
    flex: "0 0 auto",
    fontSize: "11px",
    color: FIELD_LABEL,
    paddingTop: "4px",
    whiteSpace: "nowrap",
  },
  // A square icon button rather than a text link: the row already carries a
  // name and a size, and a word of link text next to those reads as more file
  // metadata. Built as an <a> so it keeps a link's middle-click and copy-address.
  fileLink: {
    flex: "0 0 auto",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "22px",
    height: "22px",
    borderRadius: "3px",
    backgroundColor: CONTROL_BG,
    border: `1px solid ${CONTROL_BORDER}`,
    color: FIELD_LABEL,
    transition: "color 0.15s, border-color 0.15s",
    "&:hover": { color: "#4eabb1", borderColor: "#4eabb1" },
  },
  // Same button, for a link that points at an older build than the row claims.
  fileLinkStale: {
    color: "#e0b96a",
    borderColor: "rgba(224, 185, 106, 0.5)",
    "&:hover": { color: "#e0b96a", borderColor: "#e0b96a" },
  },

  grid: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px" },
  row: { display: "flex", gap: "8px", alignItems: "flex-end" },

  // ── Rename what ships ───────────────────────────────────────────────────
  // A pill rather than a line of text: this opens the only destructive-looking
  // thing in the dialog, and it used to read as a caption.
  toggle: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "3px 10px 3px 5px",
    borderRadius: "12px",
    border: `1px solid ${CONTROL_BORDER}`,
    backgroundColor: CONTROL_BG,
    color: FIELD_LABEL,
    fontSize: "11px",
    cursor: "pointer",
    transition: "color 0.15s, border-color 0.15s",
    "&:hover": { color: ACCENT, borderColor: ACCENT },
  },
  toggleOn: { color: ACCENT, borderColor: ACCENT_SOFT },
  toggleCount: {
    padding: "0 6px",
    borderRadius: "8px",
    backgroundColor: "rgba(78,171,177,0.16)",
    color: ACCENT,
    fontSize: "10px",
    fontWeight: 700,
  },
  namingHead: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: "12px",
    marginBottom: "14px",
  },
  // The eleven slots of the formula, IN THE ORDER THEY APPEAR IN THE NAME —
  // so the form is a picture of the thing it edits. Four columns fits the
  // eleven into three tidy rows at the dialog's width.
  formulaGrid: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "14px 10px" },
  slot: { position: "relative" },
  slotIndex: {
    position: "absolute",
    top: "-9px",
    right: "1px",
    fontFamily: "monospace",
    fontSize: "9px",
    color: FIELD_LABEL,
    opacity: 0.45,
  },
  // A slot that is not edited here. Shown anyway: a gap in the middle of the
  // formula would read as a token that does not exist.
  slotElsewhere: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: "2px",
    height: "40px",
    padding: "0 10px",
    borderRadius: "4px",
    border: `1px dashed ${CONTROL_BORDER}`,
    opacity: 0.75,
  },
  slotElsewhereLabel: { fontSize: "11px", color: FIELD_LABEL },
  slotElsewhereHint: { fontSize: "10px", color: ACCENT, opacity: 0.9 },
  namingDivider: { height: "1px", backgroundColor: "rgba(255,255,255,0.08)", margin: "16px 0 14px" },
  linkButton: {
    padding: 0,
    border: "none",
    background: "none",
    color: FIELD_LABEL,
    fontSize: "11px",
    cursor: "pointer",
    "&:hover": { color: ACCENT },
  },
  // The strays holding this folder's Asana id. Outlined in the warning colour
  // rather than sitting in the section as plain text: it is the one thing on
  // this screen that stops a delivery for a reason nothing else on it shows.
  claimCard: { borderColor: "rgba(224,185,106,0.35)" },
  claimList: { marginTop: "8px" },
  claimRow: { display: "flex", gap: "10px", alignItems: "baseline", padding: "3px 0" },
  claimFolder: { flex: "0 0 auto", fontSize: "11px", color: FIELD_LABEL, opacity: 0.85 },

  note: { fontSize: "11px", color: FIELD_LABEL, opacity: 0.85, marginTop: "6px", lineHeight: 1.5 },
  warning: { fontSize: "12px", color: "#e0b96a", marginTop: "6px", lineHeight: 1.5 },
  failure: { fontSize: "12px", color: "#ef9a9a", marginTop: "6px", wordBreak: "break-word" },
  ok: { fontSize: "12px", color: "#a5d6a7", marginTop: "6px" },
  busy: { display: "flex", alignItems: "center", gap: "10px", marginTop: "12px" },
  // Engine fetch, dependency install, place in the build queue — BuildWaitLine.
  waitLine: { display: "block", fontSize: "12px", color: FIELD_LABEL, marginTop: "2px" },
  step: { display: "flex", gap: "8px", fontSize: "13px", padding: "3px 0" },
  stepName: { flex: "0 0 150px", color: FIELD_LABEL },
  candidate: { display: "block", marginTop: "6px", fontSize: "12px" },
  disclosure: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    padding: 0,
    border: "none",
    background: "none",
    color: FIELD_LABEL,
    fontSize: "12px",
    cursor: "pointer",
  },

  // The Asana comment, shown the way it will read once posted.
  commentRendered: {
    marginTop: "10px",
    paddingLeft: "10px",
    borderLeft: "2px solid rgba(255,255,255,0.15)",
    fontSize: "13px",
    lineHeight: 1.6,
  },
  mention: { color: "#8ab4f8", fontWeight: 500 },
  commentBody: {
    marginTop: "8px",
    fontFamily: "monospace",
    fontSize: "11px",
    opacity: 0.75,
    wordBreak: "break-all",
    lineHeight: 1.5,
  },
});

/** The tokens the formula fills in for itself when nobody says otherwise. */
const DEFAULT_VENDOR = "acsplayin";
const DEFAULT_CONCEPT3 = "aiassisted";
const DEFAULT_SUBCONCEPT = "none";

/**
 * The formula, slot by slot, IN THE ORDER THE TOKENS APPEAR IN THE NAME.
 *
 * The form is laid out from this, so reading it left to right reads the name
 * left to right — the thing that made an eleven-token name hard to edit was
 * never the number of fields, it was not knowing which word each one was.
 *
 * `where: "file"` marks the two tokens that describe the individual creative
 * rather than the batch: a folder is deliberately a carousel, a grid and a
 * reviews, so one batch-wide value would flatten it. They are edited on the
 * file rows, and hold their place here so the formula stays whole.
 */
const FORMULA_SLOTS = [
  { key: "brand", label: "Brand", placeholder: "nolaras", help: "letters and digits" },
  { key: "vendor", label: "Vendor", placeholder: DEFAULT_VENDOR },
  { key: "creativeType", label: "Type", where: "select" },
  { key: "date", label: "Date", placeholder: "20260801", help: "YYYYMMDD" },
  { key: "startSeq", label: "No", placeholder: "1", help: "the first; rest follow" },
  { key: "assignee", label: "Creator", placeholder: "emily", help: "first name" },
  { key: "concept1", label: "Concept 1", placeholder: "product", help: "product or game" },
  { key: "category", label: "Concept 2", where: "file" },
  { key: "concept3", label: "Concept 3", placeholder: DEFAULT_CONCEPT3, help: "how it was made" },
  { key: "subconcept", label: "Subconcept", where: "file" },
  { key: "theme", label: "Theme", placeholder: "none", help: "e.g. mothersday" },
];

/** The tokens edited per file, on its own row. */
const PER_FILE_FIELDS = FORMULA_SLOTS.filter((slot) => slot.where === "file");

const EMPTY_NAMING = {
  brand: "",
  vendor: "",
  assignee: "",
  concept1: "",
  concept3: "",
  theme: "",
  date: "",
  startSeq: "",
  creativeType: "",
  // Keyed by snapshot file name: `{ "carousel_01_….json": { category, … } }`.
  perFile: {},
  // Not one of the file name's tokens: this is the DELIVERED FOLDER's name —
  // the Drive subfolder and the readable half of the S3 prefix.
  taskName: "",
};

/** A fresh blank state — `perFile` is a container, so it must not be shared. */
const emptyNaming = () => ({ ...EMPTY_NAMING, perFile: {} });

/** Only the fields the user actually filled in, per-file ones included. */
function namingPayload(naming) {
  const { perFile, ...tokens } = naming;
  const filled = _.omitBy(tokens, (value) => String(value ?? "").trim() === "");
  const files = _.omitBy(
    _.mapValues(perFile || {}, (overrides) =>
      _.omitBy(overrides, (value) => String(value ?? "").trim() === "")
    ),
    _.isEmpty
  );

  if (_.isEmpty(filled) && _.isEmpty(files)) return null;
  return {
    ...filled,
    ...(filled.startSeq ? { startSeq: Number(filled.startSeq) } : {}),
    ...(_.isEmpty(files) ? {} : { perFile: files }),
  };
}

/**
 * The form a build or a delivery was started with, from the record the server
 * sent back — or null when that asked for nothing beyond the folder's defaults.
 *
 * The record is the server's MERGED naming: it also carries the bound assignee
 * and the numbering from 01 that the folder fills in for itself. Those two go
 * back to blank here, so a restored form counts only what someone typed.
 *
 * @param {object|null} recorded  `naming` from a previewTask/deliverTask response
 * @param {object} context
 * @param {string} context.boundAssignee  what the binding fills in
 * @param {string} context.folderName     the delivered folder when nobody renames it
 * @param {string} [context.taskName]     the delivered folder the server used
 */
export function namingFromRecord(recorded, { boundAssignee = "", folderName = "", taskName = null } = {}) {
  const text = (value) => (value === null || value === undefined ? "" : String(value).trim());
  const restored = emptyNaming();

  for (const key of Object.keys(EMPTY_NAMING)) {
    if (key === "perFile" || key === "taskName") continue;
    restored[key] = text(_.get(recorded, key));
  }
  if (restored.assignee && restored.assignee === boundAssignee) restored.assignee = "";
  if (Number(restored.startSeq) === 1) restored.startSeq = "";

  for (const [fileName, overrides] of Object.entries(_.get(recorded, "perFile") || {})) {
    if (!_.isPlainObject(overrides)) continue;
    const filled = _.omitBy(_.mapValues(overrides, text), (value) => value === "");
    if (!_.isEmpty(filled)) restored.perFile[fileName] = filled;
  }

  if (text(taskName) && text(taskName) !== folderName) restored.taskName = text(taskName);

  return namingPayload(restored) ? restored : null;
}

/**
 * One folder's task as the dialog last left it, or a blank one.
 *
 * The blank form is made fresh on every call: `perFile` is a container, and two
 * folders sharing one would type into each other.
 *
 * @param {object} memory  folder path → what the dialog last showed for it
 * @param {string} folder
 */
export function taskStateFor(memory, folder) {
  const saved = (memory && memory[folder || ""]) || {};
  return {
    naming: saved.naming || emptyNaming(),
    preview: saved.preview || null,
    delivery: saved.delivery || null,
    subsetFonts: saved.subsetFonts !== false,
    serverClaims: saved.serverClaims || [],
    error: saved.error || null,
    selectedCreator: saved.selectedCreator || null,
  };
}

/**
 * A delivery name split into what each token means, or null when it does not
 * follow the formula.
 *
 * Ten tokens is the name as it was composed before `subconcept` was added
 * (2026-08-28) and reads as `subconcept: "none"`; eleven is the name since.
 * Mirrors `parseExportName` in the container's deliveryName.js, which is where
 * the formula lives — this copy exists so a rename can be previewed without
 * spending a build on it.
 */
export function parseName(exportName) {
  const tokens = String(exportName || "").replace(/\.html?$/i, "").split("_");
  if (tokens.length !== 10 && tokens.length !== 11) return null;
  if (!/^\d{8}$/.test(tokens[3]) || !/^\d{2}$/.test(tokens[4])) return null;
  if (tokens[2] !== "sip" && tokens[2] !== "mip") return null;

  const legacy = tokens.length === 10;
  return {
    brand: tokens[0],
    vendor: tokens[1],
    creativeType: tokens[2],
    date: tokens[3],
    seq: Number(tokens[4]),
    assignee: tokens[5],
    concept1: tokens[6],
    category: tokens[7],
    concept3: tokens[8],
    subconcept: legacy ? DEFAULT_SUBCONCEPT : tokens[9],
    theme: legacy ? tokens[9] : tokens[10],
  };
}

/**
 * What a SIP would be delivered as — the same composition the container does,
 * shown live so nobody has to press Build to find out.
 *
 * Three sources per token, in order: this file's own override, the batch-wide
 * one, then the name the snapshot already carries. `concept1` is kept rather
 * than re-derived from the type, so switching a file to MIP changes the one
 * token that was asked for.
 *
 * @param {number} index  the SIP's position in the folder. It is what the start
 *   number counts from: "start no 3" means the first SIP is 03 and the rest
 *   follow, exactly as deliveryName.js does it. Composing without it would show
 *   the whole batch under one number.
 */
export function previewName(sip, naming, index) {
  const parsed = parseName(sip?.exportName);
  const own = (naming.perFile && naming.perFile[sip?.fileName]) || {};
  const clean = (value) => String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const pick = (key) => clean(own[key]) || clean(naming[key]) || (parsed && parsed[key]) || null;

  const type = own.creativeType || naming.creativeType || (parsed && parsed.creativeType) || "sip";
  const eightDigits = (value) => (/^\d{8}$/.test(String(value || "")) ? String(value) : null);
  const date = eightDigits(own.date) || eightDigits(naming.date) || (parsed && parsed.date);
  const seq =
    Number(own.seq) || (naming.startSeq ? Number(naming.startSeq) + index : parsed && parsed.seq);

  const brand = pick("brand");
  const assignee = pick("assignee");
  const category = pick("category") || clean(String(sip?.fileName || "").split("_")[0]);
  const theme = pick("theme");

  if ([brand, date, seq, assignee, category, theme].some((token) => !token)) return null;
  return [
    brand,
    pick("vendor") || DEFAULT_VENDOR,
    type,
    date,
    String(seq).padStart(2, "0"),
    assignee,
    pick("concept1") || (type === "mip" ? "game" : "product"),
    category,
    pick("concept3") || DEFAULT_CONCEPT3,
    pick("subconcept") || DEFAULT_SUBCONCEPT,
    theme,
  ].join("_");
}

/** The creative type a delivered name declares. Anything unparseable reads sip. */
export function creativeTypeOf(exportName) {
  const parsed = parseName(exportName);
  return parsed && parsed.creativeType === "mip" ? "mip" : "sip";
}

/** The fields of a parsed name, in the order their tokens appear in it. */
const PARSED_FIELDS = [
  "brand", "vendor", "creativeType", "date", "seq", "assignee",
  "concept1", "category", "concept3", "subconcept", "theme",
];

/**
 * Which token positions of `name` say something different from `before`.
 *
 * Compared FIELD BY FIELD rather than position by position, because the two
 * names can be different lengths: everything on disk was named before
 * subconcept existed, and lining those up by index would mark the theme as
 * changed on every single file — a whole batch lit up for a rename that only
 * added one word.
 *
 * A name either side that does not follow the formula falls back to comparing
 * positions, which is the best that can be said about it.
 *
 * @returns {Set<number>} indexes into `name.split("_")`
 */
export function changedTokens(name, before) {
  const tokens = String(name || "").split("_");
  if (!before || before === name) return new Set();

  const now = parseName(name);
  const was = parseName(before);
  if (!now || !was) {
    const previous = String(before).split("_");
    return new Set(tokens.reduce(
      (indexes, token, index) => (token === previous[index] ? indexes : [...indexes, index]),
      []
    ));
  }

  const changed = new Set();
  PARSED_FIELDS.forEach((field, index) => {
    const a = field === "seq" ? String(now.seq).padStart(2, "0") : now[field];
    const b = field === "seq" ? String(was.seq).padStart(2, "0") : was[field];
    if (a !== b) changed.add(index);
  });
  return changed;
}

/**
 * A delivery name, read as its words.
 *
 * Eleven underscore-joined tokens is not a string anyone proof-reads
 * accurately, and proof-reading it is the entire point of the rename panel. So
 * the separators recede and the tokens that actually moved carry the accent:
 * checking a rename becomes looking at the coloured parts.
 */
function NameTokens({ name, before, classes, className }) {
  const tokens = String(name || "").split("_");
  const changed = changedTokens(name, before);

  return (
    <span className={className}>
      {tokens.map((token, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={`${index}-${token}`}>
          {index > 0 && <span className={classes.nameSeparator}>_</span>}
          <span className={changed.has(index) ? classes.nameTokenChanged : undefined}>{token}</span>
        </React.Fragment>
      ))}
    </span>
  );
}

/**
 * Iteration labels for a batch, in the order given.
 *
 * Not decoration: the preview tool uploads each file under its label as the
 * iteration name, so this is what the client sees next to every playable. The
 * container computes the same labels the same way when it delivers
 * (deliveryName.js) — what is shown here is what ships.
 *
 * EACH TYPE COUNTS FROM 01 ON ITS OWN, so two MIPs and three SIPs read MIP-01,
 * MIP-02, SIP-01, SIP-02, SIP-03. The number is positional within its type and
 * has nothing to do with the `NN` token inside the file name, which is a
 * per-category, per-day counter no delivery touches.
 *
 * Labels only — the order is the caller's.
 */
export function iterationLabels(exportNames) {
  const counters = { mip: 0, sip: 0 };
  return exportNames.map((name) => {
    const type = creativeTypeOf(name);
    counters[type] += 1;
    return `${type.toUpperCase()}-${String(counters[type]).padStart(2, "0")}`;
  });
}

/**
 * How far a delivery attempt got, named the way the UI names the steps.
 *
 * Only meaningful for an attempt that never completed: it is the difference
 * between "nothing went out" and "the files are already in Drive but nobody was
 * told", which are handled very differently by whoever picks the task up.
 */
export function reachedSteps(steps) {
  return ["plec", "drive", "asana"].filter((step) => steps && steps[step]).map((step) => STEP_LABELS[step]);
}

/**
 * What a delivery will leave behind in Drive, worked out before it is sent.
 *
 * Mirrors `driveLeftovers` in the container's taskDeliver.js, which computes the
 * same thing from the same manifest fields — the copy here exists so the warning
 * can be shown while a person is still deciding, rather than only in the result.
 * Keep the two in step.
 *
 * rclone copies over a same-named file and never deletes, so anything the last
 * delivery shipped under a name this one does not use survives; and the Drive
 * folder is named after the Asana task, so renaming the task strands the old
 * folder. Nothing removes either — that is the point of saying so.
 *
 * @param {object|null} lastDelivery  `preview.lastDelivery` from the build
 * @param {string[]} shipping         file names this build will send
 * @param {string} folderName         the Drive folder this delivery will use
 */
export function driveLeftovers(lastDelivery, shipping, folderName) {
  const previousFiles = _.get(lastDelivery, "files") || [];
  const previousFolder = _.get(lastDelivery, "driveFolder") || null;

  return {
    orphanedFiles: previousFiles.filter((file) => !shipping.includes(file)),
    movedFolder: previousFolder && previousFolder !== folderName ? previousFolder : null,
  };
}

/**
 * The files of a folder, in delivery order.
 *
 * Order only — labels are assigned later, from the name each file will actually
 * ship under, so a pending rename that changes a file's creative type moves its
 * label with it.
 */
function collectFolderSips(snapshots, folderPath) {
  const target = normalizeFolderPath(folderPath);
  const inFolder = (snapshots || []).filter(
    (snapshot) => normalizeFolderPath(snapshot.exportFolder || "") === target
  );

  return _.sortBy(inFolder, [
    (snapshot) => {
      const parsed = parseName(snapshot.exportName);
      return parsed ? parsed.seq : 99;
    },
    "fileName",
  ]);
}

/** An id as the server compares it: a trimmed string, or "" for nothing. */
function idOf(value) {
  return String(value == null ? "" : value).trim();
}

/**
 * Snapshots OUTSIDE this folder that are holding its Asana id — the reason a
 * folder every file agrees on can still deliver as "not bound to Asana".
 *
 * The server will not let a folder use a `taskId`/`asanaGid` that a snapshot
 * elsewhere in the library still carries (taskIdentity.js): two folders sharing
 * one id would mirror onto one S3 folder and comment on one Asana task. So a
 * single file left behind by a move, a copy or a folder rename takes the gid
 * with it, and every one of this folder's files reads as unbound — which
 * re-binding cannot fix, because it only writes to the files inside.
 *
 * Both fields are checked, because the server claims both: a stray whose
 * `taskId` happens to be this gid blocks it exactly as a stray `asanaGid` does.
 *
 * Mirrors resolveTaskIdentity so the dialog can say this BEFORE Submit; the
 * server sends the same list back with its refusal, and the two are merged.
 */
export function collectGidClaims(snapshots, folderPath, gid) {
  const wanted = idOf(gid);
  if (!wanted) return [];

  const target = normalizeFolderPath(folderPath);
  return (snapshots || [])
    .filter((snapshot) => normalizeFolderPath(snapshot.exportFolder || "") !== target)
    .filter(
      (snapshot) => idOf(snapshot.asanaGid) === wanted || idOf(snapshot.taskId) === wanted
    )
    .map((snapshot) => ({
      fileName: snapshot.fileName,
      exportFolder: normalizeFolderPath(snapshot.exportFolder || "") || null,
    }));
}

function creatorHelperText(source) {
  switch (source) {
    case "acs_owner":
      return "Detected from 'ACS Owner'.";
    case "requested_by":
      return "Detected from 'Requested by'.";
    case "task_creator":
      return "Detected from Asana task creator.";
    case "acs_fallback":
      return "No creator found. ACS will be mentioned unless changed.";
    default:
      return "Editable before submit.";
  }
}

/**
 * The Audience+ task: one folder, mounted by dialogBuildSubmit.js, which keeps
 * the memory and decides between this and the Ads Manager dialog.
 */
export function AudiencePlusTaskDialog({ editor, open, folder, memory, onPickFolder, onClose, createSnackbar, workflowWarning }) {
  const classes = useStyles();

  // The folder this whole component belongs to: it is mounted for one folder
  // and replaced when another is picked, so it never changes under the state.
  const selectedFolder = folder;
  const saved = useRef(null);
  if (!saved.current) saved.current = taskStateFor(memory.current, folder);

  const [isBinding, setIsBinding] = useState(false);
  // ── This folder's task, as it was last left ───────────────────────────────
  // Build and Submit outlive this dialog (useSnapshotJobs), so it is routinely
  // closed mid-job and opened again to pick the result up. Rebuilding the view
  // from nothing on every open lost the override beside a build made WITH it,
  // which let Submit rename the files back, and lost the last delivery, which
  // is what makes the button read "Re-deliver" (2026-09-11).
  const [preview, setPreview] = useState(saved.current.preview);
  const [delivery, setDelivery] = useState(saved.current.delivery);
  const [subsetFonts, setSubsetFonts] = useState(saved.current.subsetFonts);
  const [naming, setNaming] = useState(saved.current.naming);
  // Who the Asana comment credits. Remembered with the rest: it is picked once
  // and the dialog is closed and reopened several times before Submit.
  const [selectedCreator, setSelectedCreator] = useState(saved.current.selectedCreator);
  // Files the SERVER named when it refused to deliver, so its refusal explains
  // itself even where the tree's own reading of the library disagrees. Merged
  // with what this dialog works out for itself — see `gidClaims`.
  const [serverClaims, setServerClaims] = useState(saved.current.serverClaims);
  const [error, setError] = useState(saved.current.error);

  // ── How this folder is being LOOKED at ────────────────────────────────────
  // Never remembered, and gone with the folder it belonged to.
  const [isResendOpen, setIsResendOpen] = useState(false);
  const [isDiscardClaimsOpen, setIsDiscardClaimsOpen] = useState(false);
  const [namingOpen, setNamingOpen] = useState(false);
  const [rawCommentOpen, setRawCommentOpen] = useState(false);
  // An already-bound folder needs nothing from this section, so it stays folded
  // away until someone wants to change the binding.
  const [asanaOpen, setAsanaOpen] = useState(false);
  const [asanaQuery, setAsanaQuery] = useState("");
  const [asanaTasks, setAsanaTasks] = useState(null); // null = not loaded yet
  const [asanaPick, setAsanaPick] = useState("");
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [creatorContext, setCreatorContext] = useState(null);
  const [creatorOptions, setCreatorOptions] = useState([]);
  const [isLoadingCreators, setIsLoadingCreators] = useState(false);
  const [asanaCandidates, setAsanaCandidates] = useState([]);
  const [isRepairing, setIsRepairing] = useState(false);

  // Whether this folder's task is still the one on screen. A folder switch
  // replaces this component, so a bind or a task list that answers afterwards
  // belongs to a folder nobody is looking at any more — see `runBind`.
  const isCurrent = useRef(true);
  useEffect(
    () => () => {
      isCurrent.current = false;
    },
    []
  );

  // Kept as it changes, so closing the dialog is never what clears it.
  useEffect(() => {
    memory.current[selectedFolder] = {
      naming,
      preview,
      delivery,
      subsetFonts,
      serverClaims,
      error,
      selectedCreator,
    };
  }, [memory, selectedFolder, naming, preview, delivery, subsetFonts, serverClaims, error, selectedCreator]);

  // The Asana search is how the folder is being looked at, not part of it, so
  // each open starts it folded and empty.
  useEffect(() => {
    if (!open) return;
    setAsanaCandidates([]);
    setAsanaQuery("");
    setAsanaTasks(null);
    setAsanaPick("");
    setNamingOpen(false);
  }, [open]);

  const folders = useMemo(() => collectFolderPaths(editor.snapshots), [editor.snapshots]);
  const sips = useMemo(
    () => collectFolderSips(editor.snapshots, selectedFolder),
    [editor.snapshots, selectedFolder]
  );

  // The identity the server will resolve, shown before anything is pressed so
  // an unbound folder is visible rather than a surprise at delivery time.
  const boundGid = useMemo(() => {
    const gids = _.uniq(sips.map((sip) => sip.asanaGid).filter(Boolean));
    return gids.length === 1 ? gids[0] : null;
  }, [sips]);

  useEffect(() => {
    if (!open || !selectedFolder || !boundGid) return;
    let cancelled = false;
    setIsLoadingCreators(true);
    fetchAsanaCreatorContext(editor.baseID, selectedFolder).then((response) => {
      if (cancelled) return;
      if (!response.ok) return;
      const data = response.data || {};
      const options = _.uniqBy([data.creator, ...(data.creators || [])]
        .filter((item) => item?.gid && item?.email), "gid")
        .sort((a, b) => String(a.name).localeCompare(String(b.name)));
      setCreatorContext(data);
      setCreatorOptions(options);
      // Detected only where nothing was chosen: this runs again on every reopen,
      // and overwriting would quietly undo a pick made before the dialog was
      // last closed.
      setSelectedCreator(
        (current) => current || options.find((item) => item.gid === data.creator?.gid) || null
      );
    }).catch(() => {
      if (!cancelled) setCreatorContext(null);
    }).finally(() => {
      if (!cancelled) setIsLoadingCreators(false);
    });
    return () => { cancelled = true; };
  }, [open, selectedFolder, boundGid, editor.baseID]);

  // The strays holding this folder's gid, from both readings of the library:
  // this dialog's own, so the warning is on screen before anything is pressed,
  // and the server's, which is the one that actually decided. Deduped by file
  // name — the usual case is the same file in both lists.
  const gidClaims = useMemo(() => {
    const local = collectGidClaims(editor.snapshots, selectedFolder, boundGid);
    return _.uniqBy([...local, ...serverClaims], "fileName");
  }, [editor.snapshots, selectedFolder, boundGid, serverClaims]);

  // Once bound, the Asana task's own name is what the delivered folder is
  // called — the editor folder's name stops mattering for delivery.
  const folderName = useMemo(() => {
    const names = _.uniq(sips.map((sip) => sip.taskName).filter(Boolean));
    return names.length === 1 ? names[0] : selectedFolder.split("/").pop() || "";
  }, [sips, selectedFolder]);

  /** What the delivery folder will actually be called, override included. */
  const overriddenFolderName = naming.taskName.trim() || folderName;

  // Who the files are credited to: the first name of whoever opened the bound
  // Asana task, stamped onto the snapshots when the folder was bound. The
  // snapshots' own names were composed before any of that was known, so they
  // usually read "agent" — this is what replaces it, on the server too.
  const boundAssignee = useMemo(() => {
    const tokens = _.uniq(sips.map((sip) => sip.assignee).filter(Boolean));
    return tokens.length === 1 ? tokens[0] : "";
  }, [sips]);

  // What actually ships: the fields as typed, with the two the folder fills in
  // for itself — the bound task's assignee, and numbering from 01 by position,
  // because a snapshot's own NN counts per category per day and a mixed batch
  // would ship with repeats. The server does the same merge, so what is
  // previewed here is what the build and the delivery use.
  const effectiveNaming = useMemo(
    () => ({
      ...naming,
      assignee: naming.assignee.trim() || boundAssignee,
      startSeq: naming.startSeq.trim() || 1,
    }),
    [naming, boundAssignee]
  );

  const unnamed = sips.filter((sip) => !sip.exportName);

  // The two jobs this folder can have running, held by the editor rather than
  // by this dialog (see useSnapshotJobs). Read per folder on purpose: another
  // folder's build is none of this view's business, and the container builds
  // the two of them side by side.
  const buildJob = editor.jobs.get("previewTask", selectedFolder);
  const deliverJob = editor.jobs.get("deliverTask", selectedFolder);
  const isPreviewing = _.get(buildJob, "state") === "running";
  const isDelivering = _.get(deliverJob, "state") === "running";
  const runningJob = isPreviewing ? buildJob : isDelivering ? deliverJob : null;
  const progress = editor.jobs.progressOf(runningJob);
  const connectionDown = _.get(runningJob, "connectionDown") || null;

  // A delivery whose end nobody saw. The three steps run in the container and
  // finish with or without us, so the next Submit asks first — a second one
  // posts a second Asana comment. Kept by the editor, because closing this
  // dialog is not an answer to the question it raises.
  const unresolvedDelivery = editor.jobs.unresolvedDeliveries[selectedFolder] || null;

  // Everything that writes to this folder waits for this folder's own jobs.
  // Closing the dialog never does — that is the whole point of the jobs living
  // outside it.
  const isBusy = isPreviewing || isDelivering || isBinding || isRepairing;
  const links = _.get(preview, "links", []);

  /**
   * How many tokens are being overridden — the badge on the closed panel.
   *
   * Counted from what is TYPED, not from `effectiveNaming`: the assignee the
   * binding fills in and the numbering every batch gets are not somebody's
   * override, and a panel that reads "2" the moment it is opened would say the
   * folder is being renamed when nothing has been touched.
   */
  const overrideCount = useMemo(() => {
    const payload = namingPayload(naming) || {};
    const perFile = payload.perFile || {};
    return (
      _.size(_.omit(payload, "perFile")) +
      _.sumBy(Object.values(perFile), (overrides) => _.size(overrides))
    );
  }, [naming]);

  /** Override one token of one file. Keyed by the snapshot's own file name. */
  function setPerFile(fileName, key, value) {
    setNaming((current) => ({
      ...current,
      perFile: {
        ...current.perFile,
        [fileName]: { ..._.get(current.perFile, fileName, {}), [key]: value },
      },
    }));
  }

  /**
   * One row per file: the name it will ship under, and its iteration label.
   *
   * The name comes from the build when there is one, and from the same formula
   * the container uses when there is not — so an override can be checked
   * without spending a build on it. The label follows that name rather than the
   * snapshot's current one, which is what makes switching the type in "Rename
   * what ships" re-label the list as you type.
   */
  const fileRows = useMemo(() => {
    const rows = sips.map((sip, index) => {
      const link = links.find((entry) => entry.snapshot === sip.fileName);
      const finalName = link
        ? link.file.replace(/\.html$/i, "")
        : (namingPayload(effectiveNaming) && previewName(sip, effectiveNaming, index)) ||
        sip.exportName ||
        sip.fileName;
      // What the per-file fields show when nobody has typed in them: the token
      // the file already carries, so an empty box reads as "unchanged" rather
      // than as "missing". A snapshot with no delivery name falls back to the
      // same rule the pipeline uses — its file name's first word.
      const parsed = parseName(sip.exportName);
      const current = {
        category: parsed ? parsed.category : String(sip.fileName || "").split("_")[0],
        subconcept: parsed ? parsed.subconcept : DEFAULT_SUBCONCEPT,
      };
      return {
        sip,
        link,
        finalName,
        current,
        renamed: finalName !== (sip.exportName || sip.fileName),
      };
    });

    const labels = iterationLabels(rows.map((row) => row.finalName));
    return rows.map((row, index) => ({ ...row, label: labels[index] }));
  }, [sips, links, effectiveNaming]);

  // "2 MIPs · 3 SIPs" rather than a single count under one name: a folder that
  // is all MIPs used to read "2 SIPs", which is the same mistake the labels made.
  const typeSummary = useMemo(() => {
    const counts = { MIP: 0, SIP: 0 };
    for (const row of fileRows) counts[row.label.slice(0, 3)] += 1;
    return ["MIP", "SIP"]
      .filter((type) => counts[type] > 0)
      .map((type) => `${counts[type]} ${type}${counts[type] === 1 ? "" : "s"}`)
      .join(" · ");
  }, [fileRows]);
  // What the build weighs. Every byte of it ships — a SIP is one self-contained
  // html — so both halves are worth seeing: the file that is too heavy, and the
  // batch total the client receives.
  const totalBytes = _.sumBy(links, (link) => link.sizeBytes || 0);
  const freshLinks = links.filter((link) => !link.stale);

  /**
   * The failure, as the line that stays on screen under the buttons.
   *
   * Only the line: a job announces its own failure through the snackbar as it
   * happens, since by then this dialog may well be closed. Saying it twice to
   * whoever did stay is worse than saying it once.
   *
   * @param {boolean} repeatable  whether simply pressing the button again is a
   *   safe answer. True for a build (it overwrites its own artifacts), false
   *   for a delivery (it posts another Asana comment) — see describeJobFailure.
   */
  function reportFailure(response, fallback, { repeatable = true } = {}) {
    setError(describeJobFailureWithReasons(response, { fallback, repeatable }));
  }

  /**
   * Open every freshly built SIP, one tab each — on a click, never on its own.
   * A build used to do this the moment it finished, which decides for the
   * reviewer: a batch of ten takes over the browser, and a browser is free to
   * refuse windows nobody visibly asked for.
   *
   * Only what was just built: a stale link opens perfectly well while showing
   * an older build, so putting it in front of someone reviewing is worse than
   * not opening it at all — it stays in the list, labelled, for anyone who
   * still wants to look.
   */
  function openPreviews() {
    for (const link of freshLinks) window.open(link.url, "_blank", "noreferrer");
  }

  /**
   * Start the build and stop waiting for it.
   *
   * The job belongs to the editor from here on: it keeps polling with this
   * dialog closed, announces its own outcome, and leaves its result for
   * whenever this folder is looked at again — see the effect below.
   */
  function runPreview() {
    if (!selectedFolder || sips.length === 0) return;

    setError(null);
    setDelivery(null);

    const folder = selectedFolder;
    const options = { subsetFonts, naming: namingPayload(effectiveNaming) };
    editor.jobs.start({
      type: "previewTask",
      scope: folder,
      run: async (hooks) => {
        // Inside the job rather than before it: starting claims the folder, and
        // a flush that takes a second would otherwise leave the button live
        // long enough for a second click to start a second build.
        //
        // Whatever the editor still has queued must reach disk first, or the
        // preview would show the version on disk rather than the one on screen.
        await editor.flushSave();
        return previewTask(editor.baseID, folder, { ...options, ...hooks });
      },
    });
  }

  /**
   * Fill an untouched form from what a job was started with.
   *
   * The task memory covers a dialog closed and opened again; this covers a
   * reload, where the job comes back from the server (listJobs) and what was
   * typed does not. Anything typed since is left alone.
   */
  function restoreNaming(record) {
    const restored = namingFromRecord(_.get(record, "naming"), {
      boundAssignee,
      folderName,
      taskName: _.get(record, "taskName"),
    });
    if (restored) setNaming((current) => (namingPayload(current) ? current : restored));
  }

  // The build's result, taken up whenever this folder is on screen — which is
  // usually the moment it lands, and after a build that finished with the
  // dialog closed is whenever it is opened again.
  useEffect(() => {
    if (!open || !buildJob || buildJob.state !== "finished") return;

    const { response } = buildJob;
    editor.jobs.consume(buildJob.key);

    if (!response.ok) {
      // A build can simply be run again: it overwrites its own artifacts and
      // nothing leaves the company.
      reportFailure(response, "Build failed.");
      return;
    }
    setError(null);
    setPreview(response.data);
    restoreNaming(response.data);
  }, [open, buildJob]);

  /**
   * Send the task, and stop waiting for it.
   *
   * Like the build, the job outlives this dialog — which is exactly why the
   * "did those files go out?" question is kept by the editor rather than here.
   *
   * @param {boolean} dryRun  compose everything and send nothing — the point is
   *   to read the Asana comment, and see who it tags, before it is posted.
   */
  function runDeliver(dryRun = false) {
    setError(null);

    const folder = selectedFolder;
    const options = {
      naming: namingPayload(effectiveNaming),
      ...(dryRun ? { dryRun: true } : {}),
      ...(selectedCreator ? { creator: selectedCreator } : {}),
    };
    editor.jobs.start({
      // `wasDelivered` is read now, while the banner still says what this task
      // was before this delivery changed it — the message needs the word
      // "Re-delivered" and the response does not carry it.
      meta: { dryRun, wasDelivered },
      type: "deliverTask",
      scope: folder,
      run: (hooks) => deliverTask(editor.baseID, folder, { ...options, ...hooks }),
    });
  }

  // The delivery's result, taken up the same way the build's is.
  useEffect(() => {
    if (!open || !deliverJob || deliverJob.state !== "finished") return;

    const { response, meta } = deliverJob;
    // What it was started as, or — for a job this page picked up after a reload
    // — what the container says it did.
    const dryRun = Boolean(_.get(meta, "dryRun") || _.get(response, "data.dryRun"));
    editor.jobs.consume(deliverJob.key);

    if (!response.ok) {
      const code = jobFailureCode(response);
      if (code === "ASANA_TASK_NOT_BOUND") {
        // The server names the files that took the id, when that is what
        // happened. Keeping them is what turns a dead end — "bind it", on a
        // folder that is bound — into something with a button next to it.
        const conflicts = _.flatMap(_.get(response, "error.conflicts", []), (conflict) =>
          conflict.claimedBy || []
        );
        setServerClaims(conflicts);
        setError(
          conflicts.length || gidClaims.length
            ? "This folder's Asana id is held by snapshots outside it, so the server treats the folder as unbound. Free the id in the Asana section below — re-binding does not fix it."
            : "This folder is not bound to an Asana task yet — bind it in the Asana section, then submit."
        );
        return;
      }
      if (code === "PREVIEW_REQUIRED") {
        setError("Build this task first: submitting ships the files the build produced.");
        return;
      }
      if (code === "BUILD_OUTDATED") {
        // Named file by file: "build again" alone reads like a glitch, and the
        // list is usually exactly the edit the person just made.
        const listed = ["changed", "added", "removed"]
          .map((key) => [key, _.get(response, ["error", key], [])])
          .filter(([, files]) => files.length)
          .map(([key, files]) => `${key}: ${files.join(", ")}`);
        setError(
          `The folder changed since this build (${listed.join(" · ")}). Build again — ` +
          "Submit ships the files the build produced, so it would send the old ones."
        );
        return;
      }
      if (code === "EXPORT_NAME_MISSING") {
        setError(
          `These snapshots have no metadata.exportName, so they cannot be submitted: ${_.get(
            response,
            "error.snapshots",
            []
          ).join(", ")}`
        );
        return;
      }
      // Never "try again" here: the container re-runs every step it is given
      // and posts a second Asana comment.
      reportFailure(response, "Submit failed.", { repeatable: false });
      return;
    }

    setDelivery(response.data);
    restoreNaming(response.data);

    // Keep the banner honest without forcing another build. Deliberately NOT
    // on a dry run: it reports `completedAt: null` because it stamped nothing,
    // and folding that in would erase a real delivery from the banner while
    // leaving it untouched in S3.
    if (!dryRun) {
      setPreview((current) =>
        current
          ? {
            ...current,
            lastDelivery: {
              completedAt: _.get(response, "data.delivery.completedAt") || null,
              by: _.get(response, "data.delivery.humanApproved.by", null),
              steps: _.pick(_.get(response, "data.delivery") || {}, ["plec", "drive", "asana"]),
              driveFolder: _.get(response, "data.delivery.driveFolder") || null,
              files: _.get(response, "data.delivery.files") || [],
              driveLink: _.get(response, "data.driveLink") || null,
              previewUrl: _.get(response, "data.previewUrl") || null,
            },
          }
          : current
      );
    }
  }, [open, deliverJob]);

  /**
   * Submit, unless the last one's outcome was never seen — then ask first.
   *
   * The container has no "already delivered" check: it re-uploads to Drive and
   * comments on Asana again, and the second comment is seen by the client. So
   * the one moment a person is most likely to press this twice — the moment the
   * dialog said the connection was lost — is the one moment it must not be a
   * single click.
   */
  function submitDelivery() {
    if (unresolvedDelivery) {
      setIsResendOpen(true);
      return;
    }
    runDeliver(false);
  }

  /**
   * Pull the open tasks so the user can pick instead of typing. On demand
   * rather than on open: it is a multi-second call and most folders arrive
   * already bound.
   */
  async function loadAsanaTasks() {
    setIsLoadingTasks(true);
    setError(null);
    try {
      const response = await listAsanaTasks(editor.baseID, selectedFolder);
      // The list is ONE folder's — it is sorted and preselected by whether a
      // task matches that folder's name. Dropped rather than shown once another
      // folder is on screen, or Bind would offer this folder's task for it.
      if (!isCurrent.current) return;
      if (!response.ok) {
        reportFailure(response, "Could not list Asana tasks.");
        return;
      }
      setAsanaTasks(response.data.tasks || []);
      // The folder's own name is the likely answer and sorts first; preselect
      // it so the common case is one click.
      const match = (response.data.tasks || []).find((task) => task.matchesFolder);
      setAsanaPick(match ? match.gid : "");
    } finally {
      if (isCurrent.current) setIsLoadingTasks(false);
    }
  }

  async function runBind(query) {
    setIsBinding(true);
    setError(null);
    setAsanaCandidates([]);
    try {
      // Bound to the folder this component was mounted for, never to whatever
      // is on screen when the call comes back.
      const response = await bindAsanaTask(editor.baseID, selectedFolder, query);
      if (!isCurrent.current) {
        if (response.ok) await editor.refreshSnapshots();
        return;
      }
      if (!response.ok) {
        const code = _.get(response, "error.error") || _.get(response, "error");
        if (code === "ASANA_TASK_AMBIGUOUS") {
          setAsanaCandidates(_.get(response, "error.candidates", []));
          setError("More than one open Asana task has this name — pick the right one.");
          return;
        }
        if (code === "ASANA_TASK_NOT_FOUND") {
          setError("No open Asana task matched. Paste the task's URL instead.");
          return;
        }
        reportFailure(response, "Could not bind the Asana task.");
        return;
      }

      const credited = _.get(response, "data.assignee");
      createSnackbar?.(
        `Bound to "${_.get(response, "data.task.name")}"` +
        `${credited ? `, credited to "${credited}"` : ""} — build again to move the S3 folder.`,
        "success",
        false
      );
      setAsanaQuery("");
      setAsanaTasks(null);
      setAsanaPick("");
      // The gid is now the task's identity: the next preview moves the S3
      // folder to it and sweeps the old one.
      await editor.refreshSnapshots();
      setPreview(null);
      // The creator belonged to the task this folder has just stopped being:
      // the new binding has its own, and the fetch below only fills a blank.
      setSelectedCreator(null);
    } finally {
      if (isCurrent.current) setIsBinding(false);
    }
  }

  /**
   * Hand the id back: the strays keep their content and their place in the
   * tree, they just stop belonging to a delivery task.
   *
   * The mild half of the pair, and the one to reach for first — nothing is
   * moved and nothing is lost. A stray that turns out to still matter gets its
   * ids back the next time its own folder is built.
   */
  async function releaseClaims() {
    const fileNames = gidClaims.map((claim) => claim.fileName);
    if (!fileNames.length) return;

    setIsRepairing(true);
    setError(null);
    try {
      // The hook reports its own failures and refreshes the tree, which is what
      // recomputes `gidClaims`.
      const result = await editor.mutateSnapshot("clearTaskBinding", { fileNames });
      if (!result || !isCurrent.current) return;

      setServerClaims([]);

      // A file the server could not find is one the tree still lists — the
      // library moved under this dialog, and the claim may well be gone with it.
      const missing = result.missing || [];
      if (missing.length) {
        setError(`No longer in the library, so nothing was cleared on them: ${missing.join(", ")}`);
      }

      const freed = (result.affected || []).length;
      createSnackbar?.(
        freed
          ? `Freed the Asana id — ${freed} snapshot${freed === 1 ? "" : "s"} released it. Submit again.`
          : "Those snapshots were not holding the id any more.",
        "success",
        false
      );
    } finally {
      if (isCurrent.current) setIsRepairing(false);
    }
  }

  /**
   * Take the strays out of the tree: each one moves to `badones/` — the
   * library's trash, where a discarded snapshot goes. Nothing is deleted.
   *
   * One request per file rather than a batch, because that is what `discard`
   * is — and because a locked one refuses individually, which is worth saying
   * by name. The tree is refreshed once, at the end.
   */
  async function discardClaims() {
    const fileNames = gidClaims.map((claim) => claim.fileName);
    if (!fileNames.length) return;

    setIsRepairing(true);
    setError(null);
    try {
      await editor.flushSave();

      const discarded = [];
      const locked = [];
      const failed = [];
      for (const fileName of fileNames) {
        const response = await mutateSnapshot(editor.baseID, "discard", { fileName });
        if (response.ok) discarded.push(fileName);
        else if ((_.get(response, "error.error") || response.error) === "SNAPSHOT_IS_LOCKED") {
          locked.push(fileName);
        } else failed.push(fileName);
      }

      setServerClaims([]);
      await editor.refreshSnapshots();
      if (!isCurrent.current) return;

      if (locked.length || failed.length) {
        setError(
          [
            locked.length
              ? `Still holding the id because they are locked — unlock them in the tree first: ${locked.join(", ")}`
              : null,
            failed.length ? `Could not be moved to trash: ${failed.join(", ")}` : null,
          ]
            .filter(Boolean)
            .join(" · ")
        );
      }
      if (discarded.length) {
        createSnackbar?.(
          `Moved ${discarded.length} snapshot${discarded.length === 1 ? "" : "s"} to trash (badones/).`,
          "success",
          false
        );
      }
    } finally {
      if (isCurrent.current) setIsRepairing(false);
    }
  }

  const asanaStep = _.get(delivery, "steps.asana") || null;
  const staleCount = links.filter((link) => link.stale).length;

  // What happened to this task the last time it went out, from the manifest the
  // build read back (previewTask). `completedAt` is the only thing that means
  // delivered — an attempt that stopped at the Asana comment put files in Drive
  // that nobody was told about, and has to read differently.
  const lastDelivery = _.get(preview, "lastDelivery") || null;
  const wasDelivered = Boolean(_.get(lastDelivery, "completedAt"));
  const partialSteps = lastDelivery && !wasDelivered ? reachedSteps(lastDelivery.steps) : [];

  // What this delivery will NOT overwrite, named while there is still a person
  // looking at it. Nothing removes any of it — see driveLeftovers.
  const { orphanedFiles, movedFolder } = useMemo(
    () => driveLeftovers(lastDelivery, links.map((link) => link.file), overriddenFolderName),
    [lastDelivery, links, overriddenFolderName]
  );
  const optionsChanged =
    preview &&
    (preview.subsetFonts !== subsetFonts ||
      !_.isEqual(_.omitBy(preview.naming || {}, _.isNil), namingPayload(effectiveNaming) || {}));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ style: { backgroundColor: PANEL_BG } }}
    >
      <DialogTitle>Build &amp; submit task</DialogTitle>
      <DialogContent>
        {workflowWarning && (
          <Typography className={classes.warning} title={workflowWarning}>
            Asana could not be checked. Using the default Audience+ workflow.
          </Typography>
        )}
        {/* ── What this is ─────────────────────────────────────────────────── */}
        <TextField
          select
          fullWidth
          size="small"
          SelectProps={{ native: true }}
          InputLabelProps={{ shrink: true }}
          label="Folder"
          value={selectedFolder}
          onChange={(event) => onPickFolder(event.target.value)}
        >
          <option value="">Select a folder…</option>
          {folders.map((folder) => (
            <option key={folder} value={folder}>
              {folder}
            </option>
          ))}
        </TextField>

        {selectedFolder && (
          <div className={classes.section}>
            <div className={classes.cardRaised}>
              <div className={classes.summaryName}>{overriddenFolderName}</div>
              <div className={classes.chips}>
                <span className={classes.chip}>
                  {typeSummary || `${sips.length} file${sips.length === 1 ? "" : "s"}`}
                </span>
                {/* Not simply "does this folder name a gid": it can name one and
                    still be refused, because a snapshot outside the folder is
                    holding that id. A green chip over that is the whole reason
                    the refusal reads as nonsense — see the Asana section. */}
                <span
                  className={`${classes.chip} ${boundGid && gidClaims.length === 0 ? classes.chipOk : classes.chipWarn
                    }`}
                >
                  {gidClaims.length
                    ? `Asana id held by ${gidClaims.length} other file${gidClaims.length === 1 ? "" : "s"}`
                    : boundGid
                      ? `Asana ${boundGid}`
                      : "not bound to Asana"}
                </span>
                {preview && (
                  <span
                    className={`${classes.chip} ${staleCount ? classes.chipWarn : classes.chipOk}`}
                  >
                    {staleCount ? `${staleCount} stale build` : `${links.length} built`}
                  </span>
                )}
                {unnamed.length > 0 && (
                  <span className={`${classes.chip} ${classes.chipWarn}`}>
                    {unnamed.length} unnamed
                  </span>
                )}
              </div>
              {folderName !== (selectedFolder.split("/").pop() || "") && (
                <div className={classes.note}>
                  Delivered under the Asana task&apos;s name, not this folder&apos;s.
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Already delivered ────────────────────────────────────────────
            Shown after a build, because that is when the manifest is read back
            and also the only moment Submit is reachable. The point is that a
            second delivery should never be a surprise: it re-uploads, it
            re-comments, and it may leave files behind in Drive. */}
        {lastDelivery && (
          <div className={classes.section}>
            <div className={classes.sectionHead}>
              <span className={classes.sectionTitle}>
                {wasDelivered ? "Already delivered" : "Last delivery did not finish"}
              </span>
            </div>
            <div className={classes.card}>
              {wasDelivered ? (
                <Typography className={classes.ok} style={{ marginTop: 0 }}>
                  Delivered {lastDelivery.by ? `by ${lastDelivery.by} ` : ""}
                  on {formatStamp(lastDelivery.completedAt)}.
                </Typography>
              ) : (
                <Typography className={classes.warning} style={{ marginTop: 0 }}>
                  {partialSteps.length
                    ? `An earlier attempt got as far as ${partialSteps.join(", ")} and stopped.`
                    : "An earlier attempt was started and did not complete."}
                </Typography>
              )}

              <div className={classes.note}>
                Delivering again re-uploads to the AppLovin preview tool and Google Drive and
                posts a new Asana comment, so every link points at the build above.
              </div>

              {(lastDelivery.driveLink || lastDelivery.previewUrl) && (
                <div className={classes.note}>
                  Previous:{" "}
                  {lastDelivery.driveLink && (
                    <a href={lastDelivery.driveLink} target="_blank" rel="noreferrer">
                      Drive folder
                    </a>
                  )}
                  {lastDelivery.driveLink && lastDelivery.previewUrl ? " · " : ""}
                  {lastDelivery.previewUrl && (
                    <a href={lastDelivery.previewUrl} target="_blank" rel="noreferrer">
                      Preview
                    </a>
                  )}
                </div>
              )}

              {/* Nothing deletes these — see taskDeliver.js. Naming them here,
                  before the button is pressed, is the whole remedy. */}
              {orphanedFiles.length > 0 && (
                <Typography className={classes.warning}>
                  {orphanedFiles.length} file(s) from the last delivery ship under a different
                  name now and will NOT be overwritten. They stay in Drive until someone removes
                  them by hand:{" "}
                  <span className={classes.fileName}>{orphanedFiles.join(", ")}</span>
                </Typography>
              )}
              {movedFolder && (
                <Typography className={classes.warning}>
                  The last delivery went to the Drive folder &quot;{movedFolder}&quot;; this one
                  goes to &quot;{overriddenFolderName}&quot;. The old folder keeps its files.
                </Typography>
              )}
            </div>
          </div>
        )}

        {/* ── Files ────────────────────────────────────────────────────────── */}
        <div className={classes.section}>
          <div className={classes.sectionHead}>
            <span className={classes.sectionTitle}>Files, in delivery order</span>
            <span style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
              {preview && staleCount > 0 && (
                <span className={classes.chipWarn} style={{ fontSize: "11px" }}>
                  amber = an earlier build
                </span>
              )}
              <TaskReviewButton
                baseID={editor.baseID}
                title={overriddenFolderName}
                entries={fileRows.filter((row) => row.link?.url).map((row) => ({
                  url: row.link.url,
                  label: row.label,
                }))}
                disabled={isBusy}
              />
              {totalBytes > 0 && (
                <span className={classes.chip}>
                  {links.length} file{links.length === 1 ? "" : "s"} · {formatBytes(totalBytes)}
                </span>
              )}
              {/* Sits on the list it renames: opening it puts two fields on
                  every row below, which read as nothing at all from the other
                  side of the dialog. */}
              <button
                type="button"
                className={`${classes.toggle} ${overrideCount ? classes.toggleOn : ""}`}
                onClick={() => setNamingOpen((open_) => !open_)}
              >
                {namingOpen ? (
                  <ExpandMoreIcon style={{ fontSize: "15px" }} />
                ) : (
                  <ChevronRightIcon style={{ fontSize: "15px" }} />
                )}
                Rename what ships
                {overrideCount > 0 && <span className={classes.toggleCount}>{overrideCount}</span>}
              </button>
            </span>
          </div>
          <div className={classes.card}>
            {sips.length === 0 && <div className={classes.note}>This folder has no snapshots.</div>}
            {fileRows.map(({ sip, link, finalName, renamed, current, label }, index) => {
              return (
                <div
                  className={`${classes.fileEntry} ${index ? classes.fileRowDivided : ""}`}
                  key={sip.fileName}
                >
                  <div className={classes.fileRow}>
                    <span className={classes.fileLabel}>{label}</span>
                    {/* The name it will ship under, with the words that moved
                        picked out — so a rename is checked by looking rather
                        than by comparing two long strings character by
                        character. */}
                    <NameTokens
                      className={classes.fileName}
                      classes={classes}
                      name={finalName}
                      before={renamed ? sip.exportName : null}
                    />
                    {link && formatBytes(link.sizeBytes) && (
                      <span className={classes.fileSize}>{formatBytes(link.sizeBytes)}</span>
                    )}
                    {link && (
                      <a
                        className={`${classes.fileLink} ${link.stale ? classes.fileLinkStale : ""}`}
                        href={link.url}
                        target="_blank"
                        rel="noreferrer"
                        title={
                          link.stale
                            ? "This SIP failed to build — opens an earlier build"
                            : "Open this SIP"
                        }
                      >
                        <LaunchIcon style={{ fontSize: "14px" }} />
                      </a>
                    )}
                  </div>

                  {/* The two tokens that are this creative's rather than the
                      batch's. Shown with the rest of the renaming, so the file
                      list stays a list until someone is actually renaming. */}
                  {namingOpen && (
                    <div className={classes.perFileRow}>
                      <span className={classes.perFileLabel}>This file</span>
                      {PER_FILE_FIELDS.map((field) => (
                        <TextField
                          key={field.key}
                          className={classes.perFileField}
                          size="small"
                          label={field.label}
                          InputLabelProps={{ shrink: true }}
                          placeholder={current[field.key] || "(keep)"}
                          value={_.get(naming, ["perFile", sip.fileName, field.key], "")}
                          disabled={isBusy}
                          onChange={(event) =>
                            setPerFile(sip.fileName, field.key, event.target.value)
                          }
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {unnamed.length > 0 && (
            <Typography className={classes.warning}>
              {unnamed.length} snapshot(s) carry no delivery name — they can be built under their
              file name, but submitting will refuse them.
            </Typography>
          )}

          {/* Delivery names come from each snapshot's own metadata, built from
              the task's facts. When those facts were wrong — the brand changed,
              the batch is being renumbered, someone else is credited — these
              fields rename what ships. Empty fields change nothing. */}
          {namingOpen && (
            <div className={classes.cardRaised} style={{ marginTop: "10px" }}>
              <div className={classes.namingHead}>
                <span className={classes.namingHint}>
                  Every word of the file name, in the order it appears. An empty field keeps what
                  the snapshot already says.
                </span>
                {overrideCount > 0 && (
                  <button
                    type="button"
                    className={classes.linkButton}
                    disabled={isBusy}
                    onClick={() => setNaming(emptyNaming())}
                  >
                    Clear all
                  </button>
                )}
              </div>

              <div className={classes.formulaGrid}>
                {FORMULA_SLOTS.map((slot, index) => {
                  const position = <span className={classes.slotIndex}>{index + 1}</span>;

                  // Held open, not edited here — see FORMULA_SLOTS.
                  if (slot.where === "file") {
                    return (
                      <div className={classes.slot} key={slot.key}>
                        {position}
                        <div className={classes.slotElsewhere}>
                          <span className={classes.slotElsewhereLabel}>{slot.label}</span>
                          <span className={classes.slotElsewhereHint}>on each file row</span>
                        </div>
                      </div>
                    );
                  }

                  if (slot.where === "select") {
                    return (
                      <div className={classes.slot} key={slot.key}>
                        {position}
                        <TextField
                          select
                          fullWidth
                          size="small"
                          SelectProps={{ native: true }}
                          InputLabelProps={{ shrink: true }}
                          label={slot.label}
                          value={naming.creativeType}
                          disabled={isBusy}
                          onChange={(event) =>
                            setNaming({ ...naming, creativeType: event.target.value })
                          }
                        >
                          <option value="">(keep)</option>
                          <option value="sip">SIP</option>
                          <option value="mip">MIP</option>
                        </TextField>
                      </div>
                    );
                  }

                  // Two of these are not "leave the snapshot's alone" when left
                  // empty — the folder answers them itself — so they say what
                  // they will do instead of showing an example.
                  const bound = slot.key === "assignee" && boundAssignee;
                  const help = slot.key === "startSeq" ? "empty: 01, 02… in this order" : slot.help;
                  return (
                    <div className={classes.slot} key={slot.key}>
                      {position}
                      <TextField
                        fullWidth
                        size="small"
                        label={slot.label}
                        placeholder={bound || slot.placeholder}
                        helperText={bound ? "from the bound Asana task" : help}
                        value={naming[slot.key]}
                        disabled={isBusy}
                        onChange={(event) => setNaming({ ...naming, [slot.key]: event.target.value })}
                      />
                    </div>
                  );
                })}
              </div>

              <div className={classes.namingDivider} />

              {/* Not one of the eleven: this names the FOLDER the files are
                  delivered into, which is why it sits below the rule rather
                  than as a twelfth box in the grid. */}
              <TextField
                fullWidth
                size="small"
                label="Delivered folder"
                placeholder={preview?.taskName || folderName || "the Asana task's name"}
                helperText="Drive subfolder, and the readable half of the S3 folder — not part of the file name"
                value={naming.taskName}
                disabled={isBusy}
                onChange={(event) => setNaming({ ...naming, taskName: event.target.value })}
              />

              <div className={classes.note}>
                Applied by the build, so the files you look at are the files that ship. The
                snapshots themselves are never touched.
              </div>
            </div>
          )}
        </div>

        {/* ── Build options ────────────────────────────────────────────────── */}
        {boundGid && (
          <div className={classes.section}>
            <Autocomplete
              options={creatorOptions}
              value={selectedCreator}
              getOptionLabel={(option) => option?.name || ""}
              // By gid: a creator remembered from an earlier open is a different
              // object than the one this open fetched, and identity would read
              // it as a value that is not in the list.
              getOptionSelected={(option, value) => option.gid === _.get(value, "gid")}
              loading={isLoadingCreators}
              disabled={isBusy || isLoadingCreators || !creatorOptions.length}
              disableClearable
              onChange={(_event, value) => setSelectedCreator(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  size="small"
                  label="Creator"
                  placeholder={isLoadingCreators ? "Loading creator..." : "Select creator"}
                  InputLabelProps={{ shrink: true }}
                  helperText={creatorHelperText(creatorContext?.creatorSource)}
                />
              )}
            />
          </div>
        )}

        <div className={classes.section}>
          <div className={classes.sectionHead}>
            <span className={classes.sectionTitle}>Build options</span>
          </div>

          <FormControlLabel
            control={
              <Switch
                size="small"
                color="primary"
                checked={subsetFonts}
                disabled={isBusy}
                onChange={(event) => setSubsetFonts(event.target.checked)}
              />
            }
            label={<span style={{ fontSize: "13px" }}>Shrink fonts to used characters</span>}
          />

          {optionsChanged && (
            <Typography className={classes.warning}>
              A build option changed since the last build — build again, or the links show
              something else than what would ship.
            </Typography>
          )}
        </div>

        {/* ── Asana ────────────────────────────────────────────────────────── */}
        <div className={classes.section}>
          <div className={classes.sectionHead}>
            <span className={classes.sectionTitle}>Asana task</span>
            {boundGid && (
              <button
                type="button"
                className={classes.disclosure}
                onClick={() => setAsanaOpen((open_) => !open_)}
              >
                {asanaOpen ? "Hide" : "Change binding"}
              </button>
            )}
          </div>

          {/* A folder can name a task and still not be allowed to use it.
              taskIdentity refuses an id that a snapshot ELSEWHERE in the library
              still carries — two folders sharing one id would mirror onto one S3
              folder and comment on one Asana task — so one file left behind by a
              move, a copy or a folder rename takes the whole folder's binding
              with it. The dead end this replaces was a folder whose every file
              read the right gid, a Submit that said "not bound", and a re-bind
              that changed nothing (it only writes to the files inside). */}
          {gidClaims.length > 0 && (
            <div className={`${classes.card} ${classes.claimCard}`}>
              <Typography className={classes.warning} style={{ marginTop: 0 }}>
                {gidClaims.length} snapshot{gidClaims.length === 1 ? "" : "s"} outside this folder
                {gidClaims.length === 1 ? " is" : " are"} holding this folder&apos;s Asana id
                {boundGid ? ` (${boundGid})` : ""}, so the server treats the folder as unbound and
                will not post the comment. Binding again does not clear it.
              </Typography>

              <div className={classes.claimList}>
                {gidClaims.map((claim) => (
                  <div className={classes.claimRow} key={claim.fileName}>
                    <span className={classes.fileName}>{claim.fileName}</span>
                    <span className={classes.claimFolder}>
                      {claim.exportFolder || "no folder"}
                    </span>
                  </div>
                ))}
              </div>

              <div className={classes.note}>
                Freeing the id takes <code>taskId</code> and <code>asanaGid</code> off those files
                and touches nothing else — they stay where they are, and pick an id back up the
                next time their own folder is built. Moving to trash puts them in{" "}
                <code>badones/</code>, which deletes nothing either.
              </div>

              <div className={classes.row} style={{ marginTop: "10px" }}>
                <Button
                  size="small"
                  color="primary"
                  variant="contained"
                  disabled={isBusy}
                  onClick={releaseClaims}
                >
                  Free the id
                </Button>
                <Button size="small" disabled={isBusy} onClick={() => setIsDiscardClaimsOpen(true)}>
                  Move to trash
                </Button>
                {isRepairing && <CircularProgress size={16} />}
              </div>
            </div>
          )}

          {boundGid && !asanaOpen ? (
            gidClaims.length === 0 && (
              <div className={classes.note}>
                Bound to {folderName || "the task"} ({boundGid}). The comment goes there.
              </div>
            )
          ) : (
            <>
              {/* Picking beats typing: whatever is chosen is a gid, so a typo or
                  two tasks sharing a name cannot bind the wrong one. */}
              {asanaTasks === null ? (
                <Button
                  size="small"
                  disabled={isBusy || isLoadingTasks || !selectedFolder}
                  onClick={loadAsanaTasks}
                  startIcon={isLoadingTasks ? <CircularProgress size={14} /> : null}
                >
                  {isLoadingTasks ? "Loading open tasks…" : "Choose from open Asana tasks"}
                </Button>
              ) : (
                <div className={classes.row}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    SelectProps={{ native: true }}
                    InputLabelProps={{ shrink: true }}
                    label={`Open tasks (${asanaTasks.length})`}
                    value={asanaPick}
                    disabled={isBusy}
                    onChange={(event) => setAsanaPick(event.target.value)}
                  >
                    <option value="">Select a task…</option>
                    {asanaTasks.map((task) => (
                      <option key={task.gid} value={task.gid}>
                        {task.matchesFolder ? "★ " : ""}
                        {task.name}
                        {task.creator ? ` — ${task.creator}` : ""}
                      </option>
                    ))}
                  </TextField>
                  <Button disabled={isBusy || !asanaPick} onClick={() => runBind(asanaPick)}>
                    Bind
                  </Button>
                </div>
              )}

              {/* Only tasks assigned to the shared account are listed, so
                  anything else still needs its URL. */}
              <div className={classes.row} style={{ marginTop: "10px" }}>
                <TextField
                  fullWidth
                  size="small"
                  label="…or paste a task URL / id"
                  placeholder={selectedFolder || "the folder's own name"}
                  value={asanaQuery}
                  disabled={isBusy || !selectedFolder}
                  onChange={(event) => setAsanaQuery(event.target.value)}
                />
                <Button
                  disabled={isBusy || !selectedFolder}
                  onClick={() => runBind(asanaQuery.trim() || undefined)}
                >
                  Bind
                </Button>
              </div>

              {asanaCandidates.map((candidate) => (
                <a
                  className={classes.candidate}
                  key={candidate.gid}
                  href="#bind"
                  onClick={(event) => {
                    event.preventDefault();
                    runBind(candidate.gid);
                  }}
                >
                  {candidate.name} ({candidate.gid})
                </a>
              ))}
            </>
          )}
        </div>

        {/* ── What happened ────────────────────────────────────────────────── */}
        {isPreviewing && (
          <div className={classes.busy}>
            <CircularProgress size={18} />
            <div>
              <Typography style={{ fontSize: "13px" }}>
                {progress || "Building in the agent container — a couple of minutes per SIP."}
              </Typography>
              <BuildWaitLine events={_.get(buildJob, "events")} active={isPreviewing} className={classes.waitLine} />
            </div>
          </div>
        )}

        {isDelivering && (
          <div className={classes.busy}>
            <CircularProgress size={18} />
            <Typography style={{ fontSize: "13px" }}>
              {progress || "Uploading to the preview tool and Drive, then commenting on Asana…"}
            </Typography>
          </div>
        )}

        {/* The progress line above stops moving when the connection goes, which
            on its own reads as a hang. The job is untouched by any of this — it
            runs in the container — so this says what is actually happening. */}
        {isBusy && connectionDown && (
          <Typography className={classes.warning}>
            Lost contact with the server {Math.round((connectionDown.sinceMs || 0) / 1000)}s ago —
            still retrying. The job keeps running there, whether or not this stays open.
          </Typography>
        )}

        {preview && (
          <div className={classes.section}>
            {staleCount > 0 ? (
              <Typography className={classes.warning}>
                {staleCount} of {links.length} link(s) still show an EARLIER build — those SIPs
                failed to build just now. They were not opened, and submitting ships the old files.
              </Typography>
            ) : (
              <Typography className={classes.ok}>
                Built {links.length} SIP(s). The links stay valid after the next build.
              </Typography>
            )}
            {(preview.movedFrom || []).length > 0 && (
              <div className={classes.note}>
                Moved from {preview.movedFrom.join(", ")} — the old folder was removed.
              </div>
            )}
            {(preview.failed || []).map((item, index) => (
              <Typography className={classes.failure} key={`${item.snapshot}-${index}`}>
                {item.snapshot}: {item.error}
              </Typography>
            ))}
            {(preview.warnings || []).map((message) => (
              <Typography className={classes.warning} key={message}>
                {message}
              </Typography>
            ))}
          </div>
        )}

        {delivery && (
          <div className={classes.section}>
            <div className={classes.sectionHead}>
              <span className={classes.sectionTitle}>
                {delivery.dryRun ? "Test — nothing was sent" : "Delivered"}
              </span>
            </div>

            <div className={classes.card}>
              {Object.entries(delivery.steps || {}).map(([step, result]) => (
                <div className={classes.step} key={step}>
                  <span className={classes.stepName}>{STEP_LABELS[step] || step}</span>
                  {result.error ? (
                    <span style={{ color: "#ef9a9a" }}>{result.message || result.error}</span>
                  ) : (
                    <span style={{ color: "#a5d6a7" }}>done</span>
                  )}
                </div>
              ))}

              {(delivery.driveLink || delivery.previewUrl) && (
                <div className={classes.step} style={{ marginTop: "6px" }}>
                  <span className={classes.stepName}>Links</span>
                  <span>
                    {delivery.driveLink && (
                      <a href={delivery.driveLink} target="_blank" rel="noreferrer">
                        Drive folder
                      </a>
                    )}
                    {delivery.driveLink && delivery.previewUrl ? " · " : ""}
                    {delivery.previewUrl && (
                      <a href={delivery.previewUrl} target="_blank" rel="noreferrer">
                        AppLovin preview
                      </a>
                    )}
                  </span>
                </div>
              )}
            </div>

            {/* The comment, as it will read once Asana renders it. Who gets
                tagged is resolved on the server — the task's opener, or the
                person an ACS Bot task names in its notes — so this is the only
                place it can be checked before it goes out. */}
            {asanaStep && asanaStep.comment && (
              <div className={classes.card} style={{ marginTop: "10px" }}>
                <div style={{ fontSize: "12px", color: FIELD_LABEL }}>
                  Comment on <strong>{asanaStep.taskName || "the bound task"}</strong>
                  {asanaStep.taskUrl && (
                    <>
                      {" — "}
                      <a href={asanaStep.taskUrl} target="_blank" rel="noreferrer">
                        open in Asana
                      </a>
                    </>
                  )}
                </div>

                <div className={classes.commentRendered}>
                  <span className={classes.mention}>@{asanaStep.mentionName || "—"}</span>
                  <div>
                    {asanaStep.driveLink ? (
                      <a href={asanaStep.driveLink} target="_blank" rel="noreferrer">
                        Drive
                      </a>
                    ) : (
                      <span style={{ opacity: 0.6 }}>Drive (missing)</span>
                    )}
                    {" | "}
                    {asanaStep.previewUrl ? (
                      <a href={asanaStep.previewUrl} target="_blank" rel="noreferrer">
                        Preview
                      </a>
                    ) : (
                      <span style={{ opacity: 0.6 }}>Preview (missing)</span>
                    )}
                  </div>
                </div>

                {!asanaStep.mentionName && (
                  <Typography className={classes.warning}>
                    Nobody will be mentioned — the task&apos;s opener could not be resolved.
                  </Typography>
                )}
                {delivery.dryRun && !asanaStep.driveLink && (
                  <div className={classes.note}>
                    A test does not create the Drive folder, so that link fills in on submit.
                  </div>
                )}

                <button
                  type="button"
                  className={classes.disclosure}
                  style={{ marginTop: "10px" }}
                  onClick={() => setRawCommentOpen((open_) => !open_)}
                >
                  {rawCommentOpen ? "Hide" : "Show"} the exact payload
                </button>
                {rawCommentOpen && <div className={classes.commentBody}>{asanaStep.comment}</div>}
              </div>
            )}

            {(delivery.renamed || []).length > 0 && (
              <div className={classes.note}>
                {delivery.renamed.map((item) => (
                  <div key={item.sipLabel}>
                    {item.sipLabel}: {item.from} → {item.to}
                  </div>
                ))}
              </div>
            )}
            {/* Repeated from the pre-submit banner on purpose: this is the copy
                that survives in the result someone scrolls back to. */}
            {(delivery.orphanedFiles || []).length > 0 && (
              <Typography className={classes.warning}>
                Still in Drive under their old names, not overwritten:{" "}
                <span className={classes.fileName}>{delivery.orphanedFiles.join(", ")}</span>
              </Typography>
            )}
            {delivery.movedFolder && (
              <Typography className={classes.warning}>
                Delivered into a new Drive folder — &quot;{delivery.movedFolder}&quot; still holds
                the previous files.
              </Typography>
            )}
            {(delivery.warnings || []).map((message) => (
              <Typography className={classes.warning} key={message}>
                {message}
              </Typography>
            ))}
          </div>
        )}

        {/* Survives closing and reopening the dialog, unlike the error line:
            the question it raises — did those files go out? — is still open. */}
        {unresolvedDelivery && !isBusy && (
          <Typography className={classes.warning}>
            A submit for this folder ended without an answer. Check the Asana task and the Drive
            folder before sending it again.
          </Typography>
        )}

        {error && <Typography className={classes.failure}>{error}</Typography>}
      </DialogContent>

      <DialogActions>
        {/* Never disabled: the build and the delivery run in the container and
            are followed by the editor, so closing this stops nothing. */}
        <Button onClick={onClose}>Close</Button>
        <Button disabled={isBusy || !selectedFolder || sips.length === 0} onClick={runPreview}>
          Build
        </Button>
        {/* Reviewing is the point of the build, but WHEN to look at ten tabs is
            the reviewer's call, not the build's. */}
        <Button
          disabled={isBusy || freshLinks.length === 0}
          onClick={openPreviews}
          title={
            freshLinks.length
              ? `Open ${freshLinks.length} playable${freshLinks.length === 1 ? "" : "s"} in new tabs`
              : "Build first — there is nothing to open yet"
          }
        >
          Preview All
        </Button>
        {/* Composes the comment, runs Drive in --dry-run and skips the upload,
            so the whole delivery can be read before any of it leaves. */}
        <Button disabled={isBusy || !preview || links.length === 0} onClick={() => runDeliver(true)}>
          Test
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={isBusy || !preview || links.length === 0}
          onClick={submitDelivery}
        >
          {wasDelivered ? "Re-deliver" : "Submit"}
        </Button>
      </DialogActions>

      {/* Asked because the mild fix is right there next to it: freeing the id
          solves the same problem without moving anything, and these files are
          only strays as far as THIS folder is concerned. */}
      <Dialog
        open={isDiscardClaimsOpen}
        onClose={() => setIsDiscardClaimsOpen(false)}
        maxWidth="xs"
      >
        <DialogTitle>Move these snapshots to trash?</DialogTitle>
        <DialogContent>
          <Typography style={{ fontSize: "13px", lineHeight: 1.6 }}>
            {gidClaims.length} file{gidClaims.length === 1 ? "" : "s"} move to{" "}
            <code>badones/</code> — the library&apos;s trash — and leave the tree. Nothing is deleted,
            and a locked one is refused until it is unlocked.
          </Typography>
          <div className={classes.claimList}>
            {gidClaims.map((claim) => (
              <div className={classes.claimRow} key={claim.fileName}>
                <span className={classes.fileName}>{claim.fileName}</span>
                <span className={classes.claimFolder}>{claim.exportFolder || "no folder"}</span>
              </div>
            ))}
          </div>
          <Typography className={classes.note}>
            If you only want this folder to deliver, &quot;Free the id&quot; is enough — it leaves
            these files exactly where they are.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDiscardClaimsOpen(false)}>Cancel</Button>
          <Button
            color="primary"
            onClick={() => {
              setIsDiscardClaimsOpen(false);
              discardClaims();
            }}
          >
            Move to trash
          </Button>
        </DialogActions>
      </Dialog>

      {/* Only ever reached after a submit whose outcome was never seen. */}
      <Dialog open={isResendOpen} onClose={() => setIsResendOpen(false)} maxWidth="xs">
        <DialogTitle>Send this task again?</DialogTitle>
        <DialogContent>
          <Typography style={{ fontSize: "13px", lineHeight: 1.6 }}>
            The last submit lost contact with the server before it could report back. The three
            steps run in the agent container and do not stop when the dashboard stops listening,
            so they have most likely finished.
          </Typography>
          <Typography className={classes.warning}>
            Sending again re-uploads to Drive and posts a SECOND Asana comment — the client sees
            it. Check the Asana task and the Drive folder first.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsResendOpen(false)}>Cancel</Button>
          <Button
            color="primary"
            onClick={() => {
              setIsResendOpen(false);
              editor.jobs.dismissUnresolvedDelivery(selectedFolder);
              runDeliver(false);
            }}
          >
            Send again
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
